import React, { useState, useEffect } from "react";
import api from "../../services/api";
import Switch from "../UI/Switch";
import DiseaseSelect from "../UI/DiseaseSelect";
import { OTHER_DISEASE, toFormValues } from "../../constants/diseases";

const FIELD =
  "mb-3 w-full rounded-lg border border-stone-300 bg-stone-100 p-3 text-stone-500 placeholder:text-stone-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white";
const LABEL = "text-sm font-medium text-stone-700 dark:text-slate-300";

const today = () => new Date().toISOString().substring(0, 10);
const asDateInput = (value) => (value ? String(value).substring(0, 10) : "");

/**
 * The single maladie form. The calf sheet renders it inside a dialog on desktop,
 * and the /nouvelle_maladie and /modifier_maladie routes render it as a page on
 * mobile — so a field added here appears in every place at once.
 *
 * Pass `diseaseId` to edit an existing record, omit it to create a new one.
 */
function DiseaseForm({ calfID, diseaseId, onDone, onCancel }) {
  const isEdit = Boolean(diseaseId);

  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [cause, setCause] = useState("");
  const [causeOther, setCauseOther] = useState("");
  const [treatment, setTreatment] = useState("");
  const [veterinaryRequired, setVeterinaryRequired] = useState(false);
  const [antibioticsRequired, setAntibioticsRequired] = useState(false);
  const [antiinflammatoryRequired, setAntiinflammatoryRequired] = useState(false);
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;

    api
      .get("/health/" + diseaseId)
      .then((response) => {
        const data = response.data;
        const values = toFormValues(data);
        setStartDate(asDateInput(data.start_date) || today());
        setEndDate(asDateInput(data.end_date));
        setCause(values.cause);
        setCauseOther(values.cause_other);
        setTreatment(data.treatment || "");
        setVeterinaryRequired(Boolean(data.veterinary_required));
        setAntibioticsRequired(Boolean(data.antibiotics_required));
        setAntiinflammatoryRequired(Boolean(data.antiinflammatory_required));
      })
      .catch((error) => {
        console.error("There was an error fetching the disease!", error);
      });
  }, [isEdit, diseaseId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      start_date: startDate,
      end_date: endDate || null,
      cause: cause,
      cause_other: cause === OTHER_DISEASE ? causeOther : null,
      treatment: treatment,
      veterinary_required: veterinaryRequired,
      antibiotics_required: antibioticsRequired,
      antiinflammatory_required: antiinflammatoryRequired,
      fk_calf: calfID,
    };

    const request = isEdit
      ? api.put("/health/" + diseaseId, { ...payload, pk_health: diseaseId })
      : api.post("/health", payload);

    request
      .then(() => onDone())
      .catch((error) => {
        console.error("There was an error saving the disease!", error);
        setSaving(false);
      });
  };

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col">
      <label htmlFor="start_date_input" className={LABEL}>
        Date de début de la maladie
      </label>
      <input
        className={FIELD}
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        name="start_date_input"
      />

      <label htmlFor="end_date_input" className={LABEL}>
        Date de fin de la maladie
      </label>
      <input
        className={FIELD}
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        name="end_date_input"
      />

      <DiseaseSelect
        value={cause}
        otherValue={causeOther}
        onChange={setCause}
        onOtherChange={setCauseOther}
        inputClassName={FIELD}
        labelClassName={LABEL}
      />

      <label htmlFor="treatment_input" className={LABEL}>
        Traitement
      </label>
      <textarea
        className={FIELD}
        value={treatment}
        onChange={(e) => setTreatment(e.target.value)}
        placeholder="Traitement"
        name="treatment_input"
      />

      <label className={LABEL}>Médecin vétérinaire requis</label>
      <Switch checked={veterinaryRequired} onChange={setVeterinaryRequired} />

      <label className={"mt-3 " + LABEL}>Antibiotiques requis</label>
      <Switch checked={antibioticsRequired} onChange={setAntibioticsRequired} />

      <label className={"mt-3 " + LABEL}>Anti-inflammatoires requis</label>
      <Switch
        checked={antiinflammatoryRequired}
        onChange={setAntiinflammatoryRequired}
      />

      <button
        type="submit"
        disabled={isSaving}
        className="my-3 w-full rounded-lg bg-amber-400 p-3 font-medium text-white disabled:opacity-50 dark:bg-amber-600"
      >
        {isEdit ? "Enregistrer" : "Ajouter"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="mb-3 w-full rounded-lg border border-stone-300 p-3 text-sm text-stone-500 hover:text-amber-500 dark:border-slate-700 dark:text-slate-300"
      >
        Annuler
      </button>
    </form>
  );
}

export default DiseaseForm;
